import {
  CONFIG_CALL_TO_ACTION_DEFAULT,
  CONFIG_FORM_CAPTURE_DEFAULT,
  CONFIG_HOTSPOT_DEFAULT,
  DEFAULT_VALUE_CONTROL_MODE_CONFIG,
  DEFAULT_VALUE_FOLLOW_MODE_CONFIG,
  DEFAULT_VALUE_LITTLE_PLANET_CONFIG,
  DEFAULT_VALUE_VIEW_MODE_CONFIG,
} from '../constants';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectDetailProject,
  selectLanguageProjectSelected,
} from './detailProjectSelectors';
import { getTextMultiLang, isServer } from '@utils/helper';
import { selectDefaultLanguageProject } from './multiLanguageSelector';
import { selectDomain, selectIsFreeOwnerPlan } from './mainSelectors';

export const selectHotspotConfigGlobal = createSelector(
  [selectDetailProject],
  project => {
    const hotspotConfig = project?.config?.design?.hotspotConfig;
    return {
      ...CONFIG_HOTSPOT_DEFAULT,
      ...hotspotConfig,
      labelStyle: {
        ...CONFIG_HOTSPOT_DEFAULT.labelStyle,
        ...hotspotConfig?.labelStyle,
      },
    };
  },
);

export const selectPolygonConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.polygonConfig || undefined,
);

export const selectThemeConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.themeConfig || undefined,
);

/** Published tour graph -> automatic 3D dollhouse (three.js). null when the tour has none (old behaviour). */
export const selectPublishedTourGraph = createSelector(
  [selectDetailProject],
  project =>
    project?.tourGraph?.publishedVersion && project.tourGraph.publicUrl
      ? {
          version: project.tourGraph.publishedVersion,
          publicUrl: project.tourGraph.publicUrl,
        }
      : null,
);

// NOTE: isShowOnStart here drives the krpano (manual OBJ) dollhouse only; a published graph replaces it
export const selectDollhouseConfigGlobal = createSelector(
  [selectDetailProject, selectPublishedTourGraph],
  (project, publishedTourGraph) =>
    project?.config?.design?.dollhouseConfig
      ? {
          ...project?.config?.design?.dollhouseConfig,
          isShowOnStart:
            !!project?.config?.design?.dollhouseConfig?.isShowOnStart &&
            !!project.dollhouse?.obj_path &&
            !publishedTourGraph,
        }
      : undefined,
);

export const selectIsGraphDollhouseShowOnStart = createSelector(
  [selectDetailProject, selectPublishedTourGraph],
  (project, publishedTourGraph) =>
    !!publishedTourGraph &&
    !!project?.config?.design?.dollhouseConfig?.isShowOnStart,
);

export const selectPopupConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.popupConfig || undefined,
);

export const selectFormCaptureConfigGlobal = createSelector(
  [selectDetailProject],
  project => ({
    ...CONFIG_FORM_CAPTURE_DEFAULT,
    ...project?.config?.design?.formCaptureConfig,
  }),
);

export const selectCallToActionConfigGlobal = createSelector(
  [selectDetailProject],
  project => ({
    ...CONFIG_CALL_TO_ACTION_DEFAULT,
    ...project?.config?.design?.callToActionConfig,
  }),
);

export const selectMetaDataProject = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) =>
    project?.config?.marketing?.meta_data
      ? {
          ...project?.config?.marketing?.meta_data,
          title: getTextMultiLang(
            project?.config?.marketing?.meta_data?.title,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          description: getTextMultiLang(
            project?.config?.marketing?.meta_data?.description,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          favicon: isFreeOwnerPlan
            ? undefined
            : project?.config?.marketing?.meta_data?.favicon,
        }
      : undefined,
);

export const selectGATrackingProject = createSelector(
  [selectDetailProject],
  project => project?.config?.marketing?.analytic?.uid || undefined,
);

export const selectGATagTrackingProject = createSelector(
  [selectDetailProject],
  project =>
    project?.config?.marketing?.analytic?.ggTag ||
    process.env.NEXT_PUBLIC_GA_TAG_DEFAULT ||
    undefined,
);

export const selectScriptChats = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? undefined
      : project?.config?.marketing?.script_chats || undefined,
);

export const selectConfigLittlePlanetProject = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_LITTLE_PLANET_CONFIG
      : project?.config?.setting?.intro_little_planet || undefined,
);

export const selectConfigProjectionModeProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.projection_mode || undefined,
);

export const selectConfigControlModeProject = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_CONTROL_MODE_CONFIG
      : project?.config?.setting?.control_mode ||
        DEFAULT_VALUE_CONTROL_MODE_CONFIG,
);

export const selectConfigFollowModeProject = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_FOLLOW_MODE_CONFIG
      : project?.config?.setting?.follow_mode || { enable: true },
);

export const selectConfigViewModeProject = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_VIEW_MODE_CONFIG
      : project?.config?.setting?.view_mode || undefined,
);

export const selectConfigFirstSceneProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.general?.first_scene || undefined,
);

export const selectLanguageProject = createSelector(
  [selectDomain, selectDetailProject],
  (state, project) => {
    return !isServer && window.languages
      ? state.languageProjectSelected &&
        state.languageProjectSelected.countryCode &&
        window.languages[state.languageProjectSelected.countryCode]
        ? state.languageProjectSelected.countryCode
        : project?.config?.setting?.general?.language &&
          window.languages[project.config.setting.general.language]
        ? project.config.setting.general.language
        : 'GB'
      : 'GB';
  },
);

export const selectDollhouseProject = createSelector(
  [selectDetailProject],
  project => project?.dollhouse,
);
